import AddIcon from '@mui/icons-material/Add'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material'
import { type SxProps, type Theme } from '@mui/material/styles'
import { TableVirtuoso } from 'react-virtuoso'
import { type CSSProperties, type MouseEvent, useMemo, useState } from 'react'
import {
  type SaleMoving,
  useBulkDeleteSaleMovingMutation,
  useGetSaleMovingsQuery,
} from '../../store/api/saleControlApi'
import { HIDE_ON_MOBILE_INLINE, HIDE_ON_TABLET_INLINE, TABLE_PAGINATION_COMPACT } from '../../theme/responsive'
import BrandState from '../../components/BrandState'
import ConfirmDialog from '../../components/ConfirmDialog'
import {
  type ColumnFilterAnchorPosition,
  ColumnFilterIcon,
  ColumnFilterPopover,
  type ColumnFilterKind,
  DataframeBody,
  FilterBar,
  FilterButton,
  FilterPopover,
  SearchFilterContent,
  distinctColumnValues,
  filterBarBtnSx,
  inferColumnFilterKind,
  rowMatchesAllColumnFilters,
  rowMatchesSearch,
  useColumnFilters,
  useFilterPopover,
  useRowSelection,
  useRowMultiSelection,
} from '../../components/dataList'
import ResizeHandle from '../../components/ResizeHandle'
import { useColumnWidths } from '../../hooks/useColumnWidths'
import { useIsMobile } from '../../hooks/useResponsive'
import { usePagePermission } from '../../hooks/usePagePermission'
import { useSnackbar } from '../../hooks/useSnackbar'
import { getErrorMessage } from '../../utils/error'
import {
  getTableSortSuffix,
  sortTableRows,
  type TableSortState,
  toggleTableSort,
} from '../../utils/tableSort'
import { usePreferenceColumns } from '@/utils/datasheetColumns/usePreferenceColumns'
import { FuturesColumnsMenu } from '../futures/FuturesColumnsMenu'
import MovingFormDialog from './MovingFormDialog'
import MovingPrintDialog from './MovingPrintDialog'

type FilterKind = 'search'

/** Freeze the Move No. identifier column when scrolling horizontally. */
const FROZEN_KEYS: ReadonlySet<string> = new Set(['moveNo'])
const FROZEN_FALLBACK_WIDTH_DESKTOP: Record<string, number> = { moveNo: 110 }
const FROZEN_FALLBACK_WIDTH_MOBILE: Record<string, number> = { moveNo: 100 }
const SELECT_COL_WIDTH = 44

const ALL_ROWS_PAGE_SIZE = 1_000_000

const actionToolbarBtnSx: SxProps<Theme> = {
  minWidth: 0,
  px: 0.75,
  py: 0.2,
  minHeight: 26,
  fontSize: '0.72rem',
  lineHeight: 1.1,
  textTransform: 'none',
}

type ColumnKind = 'text' | 'date' | 'datetime' | 'number' | 'int'
type ColumnDef = {
  key: keyof SaleMoving
  label: string
  kind?: ColumnKind
  align?: 'right'
  filter?: ColumnFilterKind
  wrap?: boolean
  width?: number
}

/** Column order follows Access `frmCtrlSalesQryMoving` datasheet (TabIndex order,
 *  form không có ColumnOrder). CheckRow = cột tick select; Moving ID = PK. */
const TABLE_COLUMNS: ColumnDef[] = [
  { key: 'id', label: 'Moving ID', kind: 'int', align: 'right' },
  { key: 'fromWarehouse', label: 'From Warehouse', filter: 'checkboxes' },
  { key: 'toWarehouse', label: 'To Warehouse', filter: 'checkboxes' },
  { key: 'moveNo', label: 'Move No.' },
  { key: 'moveDate', label: 'Moving Date', kind: 'date' },
  { key: 'loadingDate', label: 'Loading Date', kind: 'date' },
  { key: 'netWeightKg', label: 'NW kgs', kind: 'number', align: 'right', width: 130 },
  { key: 'quality', label: 'Quality', filter: 'checkboxes' },
  { key: 'certification', label: 'Certification', filter: 'checkboxes' },
  { key: 'packaging', label: 'Package' },
  { key: 'noContTruck', label: 'No. cont/truck' },
  { key: 'truckName', label: 'Truck Co.' },
  { key: 'note', label: 'Note', wrap: true, width: 320 },
  { key: 'fullName', label: 'PIC', width: 180 },
  { key: 'dateUpdate', label: 'Date Updated', kind: 'datetime' },
  { key: 'personUpdate', label: 'Person Updated' },
]

const ACCESS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const formatAccessDate = (value: unknown): string => {
  if (!value) return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  return `${day}-${ACCESS_MONTHS[d.getMonth()]}-${d.getFullYear()}`
}

const formatAccessDateTime = (value: unknown): string => {
  if (!value) return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  const hh = `${d.getHours()}`.padStart(2, '0')
  const mm = `${d.getMinutes()}`.padStart(2, '0')
  return `${day}-${ACCESS_MONTHS[d.getMonth()]}-${d.getFullYear()} ${hh}:${mm}`
}

const formatAccessNumber = (value: number): string => {
  if (Number.isInteger(value)) return value.toLocaleString('en-US')
  return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const sumNumericColumn = (rows: SaleMoving[], key: keyof SaleMoving): number | null => {
  let total = 0
  let count = 0
  for (const row of rows) {
    const value = row[key]
    if (typeof value !== 'number' || !Number.isFinite(value)) continue
    total += value
    count += 1
  }
  return count > 0 ? total : null
}

export default function SaleControlMovingView() {
  const { canEdit } = usePagePermission()
  const isMobile = useIsMobile()
  const FROZEN_FALLBACK_WIDTH = isMobile ? FROZEN_FALLBACK_WIDTH_MOBILE : FROZEN_FALLBACK_WIDTH_DESKTOP
  const snack = useSnackbar()
  const [confirmDelete, setConfirmDelete] = useState<{ moveNos: string[]; ids: number[] } | null>(null)
  const [formState, setFormState] = useState<{ mode: 'new' | 'edit' | 'copy'; movId: number | null } | null>(null)
  const [printState, setPrintState] = useState<{ open: boolean; moveNos: string[] }>({ open: false, moveNos: [] })

  // Access frmCtrlSalesMainSubForm*.Form_Load: Delete gate bằng SCREEN_ACCESS (canEdit), KHÔNG allowlist email.
  const [bulkDeleteSaleMoving, { isLoading: deleting }] = useBulkDeleteSaleMovingMutation()

  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(-1)
  const { getWidth, startResize } = useColumnWidths('sale-moving.list')

  const [searchTerm, setSearchTerm] = useState('')
  const [sortState, setSortState] = useState<TableSortState<keyof SaleMoving> | null>(null)

  const filterPopover = useFilterPopover<FilterKind>()
  const closeFilterPopover = () => filterPopover.close()
  const openFilterPopover = (kind: FilterKind, e: MouseEvent<HTMLElement>) => filterPopover.open(kind, e)

  const columnFilters = useColumnFilters()
  const [colPopover, setColPopover] = useState<{
    key: keyof SaleMoving
    position: ColumnFilterAnchorPosition
  } | null>(null)
  const closeColPopover = () => setColPopover(null)

  const hasActiveFilters = !!searchTerm.trim() || columnFilters.hasAny
  const activeFilterCount = (searchTerm.trim() ? 1 : 0) + columnFilters.activeKeys.length
  const clearAllFilters = () => {
    setSearchTerm('')
    columnFilters.clearAll()
    setPage(0)
    closeFilterPopover()
  }

  const columnsCtrl = usePreferenceColumns({
    scope: 'sale_control',
    prefKey: 'columns',
    subKey: 'moving',
    allColumns: TABLE_COLUMNS,
    widthsTableKey: 'sale-moving.list',
  })
  const { visibleColumns, openMenu: openColMenu } = columnsCtrl

  const {
    data: list,
    isLoading,
    isFetching,
    error,
  } = useGetSaleMovingsQuery({ page: 1, pageSize: ALL_ROWS_PAGE_SIZE }, { refetchOnMountOrArgChange: true })

  const baseRows = useMemo(() => list?.items ?? [], [list])
  const filteredRows = useMemo(
    () =>
      baseRows.filter(
        (row) =>
          rowMatchesSearch(row, searchTerm, TABLE_COLUMNS) &&
          rowMatchesAllColumnFilters(row, columnFilters.filters),
      ),
    [baseRows, columnFilters.filters, searchTerm],
  )
  const sortedRows = useMemo(() => sortTableRows(filteredRows, sortState, TABLE_COLUMNS), [filteredRows, sortState])
  const rows = useMemo(() => {
    if (rowsPerPage === -1) return sortedRows
    const start = page * rowsPerPage
    return sortedRows.slice(start, start + rowsPerPage)
  }, [page, rowsPerPage, sortedRows])
  const totalRowValues = useMemo(
    () =>
      new Map(
        visibleColumns
          .filter((col) => col.kind === 'number')
          .map((col) => [col.key, sumNumericColumn(filteredRows, col.key)] as const),
      ),
    [filteredRows, visibleColumns],
  )
  const popoverColumn = useMemo<ColumnDef | null>(() => {
    if (!colPopover) return null
    return TABLE_COLUMNS.find((c) => c.key === colPopover.key) ?? null
  }, [colPopover])
  const popoverKind = useMemo<ColumnFilterKind>(
    () => (popoverColumn ? inferColumnFilterKind(popoverColumn as never, baseRows) : 'text'),
    [popoverColumn, baseRows],
  )
  const popoverOptions = useMemo(
    () => (popoverKind === 'checkboxes' && colPopover ? distinctColumnValues(baseRows, String(colPopover.key)) : undefined),
    [popoverKind, colPopover, baseRows],
  )

  // Default width per-column tự đo theo text (header + sample data) — §7.11.
  const autoColWidths = useMemo<Record<string, number>>(() => {
    const CHAR_PX = 7.2
    const HEADER_PAD = 40
    const CELL_PAD = 24
    const MIN = 84
    const MAX = 260
    const sample = filteredRows.slice(0, 400)
    const out: Record<string, number> = {}
    for (const col of visibleColumns) {
      const key = String(col.key)
      let bodyMax = 0
      for (const r of sample) {
        const raw = r[col.key]
        let s = ''
        if (raw != null) {
          if (col.kind === 'date' || col.kind === 'datetime') s = formatAccessDate(raw)
          else if (col.kind === 'number' && typeof raw === 'number') s = formatAccessNumber(raw)
          else s = String(raw)
        }
        if (s.length > bodyMax) bodyMax = s.length
      }
      const headerPx = col.label.length * CHAR_PX + HEADER_PAD
      const bodyPx = bodyMax * CHAR_PX + CELL_PAD
      const minForType = col.kind === 'date' || col.kind === 'datetime' ? 124 : MIN
      out[key] = Math.round(Math.min(MAX, Math.max(minForType, headerPx, bodyPx)))
    }
    return out
  }, [visibleColumns, filteredRows])

  const minTableWidth = useMemo(() => {
    const total = visibleColumns.reduce((sum, col) => {
      const key = String(col.key)
      const w =
        getWidth(key) ?? col.width ?? FROZEN_FALLBACK_WIDTH[key] ?? autoColWidths[key] ?? 100
      return sum + w
    }, SELECT_COL_WIDTH)
    return Math.max(720, total)
  }, [visibleColumns, getWidth, FROZEN_FALLBACK_WIDTH, autoColWidths])
  const gridKey = `${rowsPerPage}:${page}:${filteredRows.length}:${visibleColumns.length}`

  const toggleSort = (key: keyof SaleMoving) => {
    setSortState((current) => toggleTableSort(current, key))
    setPage(0)
  }

  // Access frmCtrlSalesQryMoving `checked_row` → multi-select (hook chung).
  // Select-all/all-check theo `rows` (đang hiển thị); selectedRows resolve theo
  // `baseRows` để giữ cả dòng đã tick nhưng đang bị lọc ẩn.
  const {
    selectedIds,
    setSelectedIds,
    selectedRows,
    toggle: toggleId,
    toggleAll: toggleSelectAll,
    allVisibleSelected,
    someVisibleSelected,
  } = useRowMultiSelection(rows, (r) => r.id, baseRows)
  // Focused row (click dòng = focus, click lại = bỏ focus — /convert-table §6.5;
  // tick checkbox riêng). Edit/Copy nhận target: đúng 1 dòng tick → dòng đó;
  // không thì dòng đang focus.
  const { selectedId: focusedId, toggleSelect: toggleFocus, clearSelection: clearFocus } = useRowSelection<number>()
  const focusedValidId = useMemo(
    () => (focusedId != null && baseRows.some((r) => r.id === focusedId) ? focusedId : null),
    [focusedId, baseRows],
  )
  // Tick >= 2 dòng → Edit/Copy disabled (target mơ hồ); tick đúng 1 → dòng đó;
  // không tick → dòng focus.
  const editTargetId =
    selectedRows.length > 1 ? null : selectedRows.length === 1 ? selectedRows[0].id : focusedValidId

  // ── Toolbar actions (Access frmCtrlSalesMainSubFormMoving, Left order) ─────
  const handleNew = () => {
    if (!canEdit) return
    setFormState({ mode: 'new', movId: null })
  }
  const handleEdit = () => {
    if (!canEdit) return
    if (editTargetId == null) {
      snack.warning('Please choose a record!')
      return
    }
    setFormState({ mode: 'edit', movId: editTargetId })
  }
  // Copy: web-only addition (Access main form has no btnCopy) — clone the target
  // row into a new record with a freshly suggested move_no.
  const handleCopy = () => {
    if (!canEdit) return
    if (editTargetId == null) {
      snack.warning('Please choose a record!')
      return
    }
    setFormState({ mode: 'copy', movId: editTargetId })
  }
  // Target cho Delete/Print: các dòng tick (multi); không tick dòng nào thì dòng focus.
  const deleteTargetRows = useMemo(() => {
    if (selectedRows.length > 0) return selectedRows
    const focused = focusedValidId != null ? baseRows.find((r) => r.id === focusedValidId) : undefined
    return focused ? [focused] : []
  }, [selectedRows, focusedValidId, baseRows])
  const handlePrint = () => {
    // Tick nhiều dòng → in các dòng tick; không tick → in dòng đang focus.
    const targets = deleteTargetRows
    const n = targets.length
    if (n === 0) {
      snack.warning('No Moving to Print!')
      return
    }
    if (n > 5) {
      snack.warning('Too many records to Print!')
      return
    }
    setPrintState({ open: true, moveNos: targets.map((r) => r.moveNo ?? '').filter(Boolean) })
  }
  const handleDeleteClick = () => {
    if (deleteTargetRows.length === 0) {
      snack.warning('Please choose a record!')
      return
    }
    if (deleteTargetRows.length > 10) {
      snack.warning('Cannot delete more than 10 records at once. Please tick 10 or fewer.')
      return
    }
    setConfirmDelete({
      moveNos: deleteTargetRows.map((r) => r.moveNo ?? '').filter(Boolean),
      ids: deleteTargetRows.map((r) => r.id),
    })
  }
  const handleConfirmDelete = async () => {
    if (!confirmDelete) return
    try {
      await bulkDeleteSaleMoving(confirmDelete.ids).unwrap()
      snack.success('Successfully Deleted')
      setSelectedIds(new Set())
      clearFocus()
      setConfirmDelete(null)
    } catch (err) {
      snack.error(getErrorMessage(err, 'Delete moving failed'))
    }
  }

  const cellValue = (row: SaleMoving, col: ColumnDef): string => {
    const val = row[col.key]
    if (val == null) return ''
    if (col.kind === 'date') return formatAccessDate(val)
    if (col.kind === 'datetime') return formatAccessDateTime(val)
    if (col.kind === 'int' && typeof val === 'number') return String(Math.round(val))
    if (col.kind === 'number' && typeof val === 'number') return formatAccessNumber(val)
    if (typeof val === 'number') return formatAccessNumber(val)
    return String(val)
  }

  const deleteMessage = confirmDelete
    ? confirmDelete.ids.length === 1
      ? `Do you want to delete Moving = '${confirmDelete.moveNos[0] ?? ''}'?`
      : `Do you want to delete ${confirmDelete.ids.length} records ?`
    : ''

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        '& > .app-dataframe': { flex: 1, minHeight: 0, height: 'auto' },
      }}
    >
      <FilterBar
        hasActiveFilters={hasActiveFilters}
        onClearAll={clearAllFilters}
        activeFilterCount={activeFilterCount}
        rightSlot={
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap justifyContent="flex-end" alignItems="center">
            {/* Button order follows Access `frmCtrlSalesMainSubFormMoving` (Left order):
                New · Edit · Delete · Print. All gated by SCREEN_ACCESS. Delete/Print act
                on the checkbox-selected rows (multi); Edit needs exactly one. */}
            <Button
              variant="contained"
              size="small"
              color="success"
              sx={actionToolbarBtnSx}
              disabled={!canEdit}
              onClick={handleNew}
              startIcon={<AddIcon sx={{ fontSize: 16 }} />}
              title="New"
            >
              <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>New</Box>
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="warning"
              sx={actionToolbarBtnSx}
              disabled={editTargetId == null || !canEdit}
              onClick={handleEdit}
              startIcon={<EditOutlinedIcon sx={{ fontSize: 16 }} />}
              title="Edit"
            >
              <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Edit</Box>
            </Button>
            <Button
              variant="outlined"
              size="small"
              sx={actionToolbarBtnSx}
              disabled={editTargetId == null || !canEdit}
              onClick={handleCopy}
              startIcon={<ContentCopyIcon sx={{ fontSize: 16 }} />}
              title="Copy"
            >
              <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Copy</Box>
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="error"
              sx={actionToolbarBtnSx}
              disabled={deleteTargetRows.length === 0 || !canEdit}
              onClick={handleDeleteClick}
              startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />}
              title="Delete"
            >
              <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Delete</Box>
            </Button>
            <Button
              variant="outlined"
              size="small"
              sx={{ ...actionToolbarBtnSx, color: 'primary.main', borderColor: 'primary.main' }}
              disabled={deleteTargetRows.length === 0}
              onClick={handlePrint}
              startIcon={<PrintOutlinedIcon sx={{ fontSize: 16 }} />}
              title="Pr-Moving (1–5 records)"
            >
              <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Pr-Moving</Box>
            </Button>
          </Stack>
        }
      >
        <FilterButton label="Search" active={!!searchTerm.trim()} onClick={(e) => openFilterPopover('search', e)} />
        <Button
          size="small"
          variant="outlined"
          endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
          onClick={(e) => openColMenu(e.currentTarget)}
          sx={filterBarBtnSx}
          title="Columns"
        >
          <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Columns</Box>
          <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>Cols</Box>
        </Button>
      </FilterBar>

      <Divider sx={{ mb: 0.5 }} />

      {error ? (
        <Alert severity="error" sx={{ mb: 1 }}>
          Failed to load Movings.
        </Alert>
      ) : null}

      <Box className="app-dataframe">
        <Box
          className="app-dataframe-toolbar"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 0.5,
            flexWrap: 'wrap',
            minHeight: 24,
            py: 0,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={0.4} sx={{ flexShrink: 0 }}>
            {(isLoading || isFetching) && <CircularProgress size={15} />}
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>
              Total: {filteredRows.length}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>
              Selected: {selectedIds.size}
            </Typography>
            <TablePagination
              component="div"
              count={filteredRows.length}
              page={rowsPerPage === -1 ? 0 : page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(Number(e.target.value))
                setPage(0)
              }}
              rowsPerPageOptions={[20, 50, 100, { label: 'All', value: -1 }]}
              labelRowsPerPage="Rows"
              sx={TABLE_PAGINATION_COMPACT}
            />
          </Stack>
        </Box>

        <FilterPopover
          open={filterPopover.state !== null}
          anchorEl={filterPopover.state?.anchor ?? null}
          onClose={closeFilterPopover}
          maxWidth={320}
        >
          {filterPopover.isOpen('search') && (
            <SearchFilterContent
              value={searchTerm}
              onChange={(v) => { setSearchTerm(v); setPage(0) }}
              placeholder="Move no, warehouse, quality..."
              label="Search movings"
              onClear={closeFilterPopover}
            />
          )}
        </FilterPopover>

        <ColumnFilterPopover
          open={colPopover !== null}
          anchorPosition={colPopover?.position ?? null}
          onClose={closeColPopover}
          label={popoverColumn?.label ?? ''}
          kind={(popoverKind === 'none' ? 'text' : popoverKind)}
          value={colPopover ? columnFilters.filters[String(colPopover.key)] : undefined}
          onChange={(v) => {
            if (!colPopover) return
            columnFilters.setFilter(String(colPopover.key), v)
            setPage(0)
          }}
          options={popoverOptions}
        />

        <DataframeBody loading={isLoading} fetching={isFetching}>
          {error ? (
            <BrandState
              tone="error"
              title="Could not load Movings"
              description="Please refresh the page or contact the system administrator if the issue continues."
            />
          ) : isLoading ? (
            <BrandState tone="loading" title="Loading…" description="Please wait." />
          ) : rows.length === 0 ? (
            <BrandState
              title="No Moving found"
              description="Try adjusting the table filters above."
            />
          ) : (
            <TableVirtuoso
              key={gridKey}
              style={{ height: '100%' }}
              data={rows}
              fixedHeaderContent={() => (
                <TableRow>
                  <TableCell
                    padding="checkbox"
                    className="app-table-header-cell"
                    style={{ position: 'sticky', top: 0, left: 0, zIndex: 101, width: SELECT_COL_WIDTH, minWidth: SELECT_COL_WIDTH }}
                  >
                    <Checkbox
                      size="small"
                      checked={allVisibleSelected}
                      indeterminate={!allVisibleSelected && someVisibleSelected}
                      onChange={toggleSelectAll}
                    />
                  </TableCell>
                  {visibleColumns.map((col) => {
                    const key = String(col.key)
                    const isFrozen = FROZEN_KEYS.has(key)
                    const w = getWidth(key) ?? col.width ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[key] : autoColWidths[key])
                    const headerStyle: CSSProperties = {}
                    if (w) { headerStyle.width = w; headerStyle.minWidth = w; headerStyle.maxWidth = w }
                    if (isFrozen) {
                      headerStyle.position = 'sticky'
                      headerStyle.top = 0
                      headerStyle.left = SELECT_COL_WIDTH
                      headerStyle.zIndex = 100
                    }
                    return (
                      <TableCell
                        key={col.key}
                        align={col.align}
                        className="app-table-header-cell"
                        onClick={() => toggleSort(col.key)}
                        style={Object.keys(headerStyle).length ? headerStyle : undefined}
                        sx={{
                          whiteSpace: 'nowrap',
                          cursor: 'pointer',
                          userSelect: 'text',
                          ...(isFrozen ? {} : { position: 'relative' }),
                        }}
                      >
                        {col.label}
                        {getTableSortSuffix(sortState, col.key)}
                        <ColumnFilterIcon
                          active={columnFilters.isActive(key)}
                          onClick={(position) => setColPopover({ key: col.key, position })}
                          ariaLabel={`Filter ${col.label}`}
                        />
                        <ResizeHandle onMouseDown={(e) => startResize(key, e)} />
                      </TableCell>
                    )
                  })}
                </TableRow>
              )}
              fixedFooterContent={() => (
                <TableRow>
                  <TableCell className="app-sticky-col" padding="checkbox" style={{ width: SELECT_COL_WIDTH }} sx={{ bgcolor: 'background.paper', borderTop: (t) => `1px solid ${t.palette.divider}` }} />
                  {visibleColumns.map((col, index) => {
                    const key = String(col.key)
                    const isFrozen = FROZEN_KEYS.has(key)
                    const total = totalRowValues.get(col.key)
                    const val = total == null ? '' : formatAccessNumber(total)
                    const w = getWidth(key) ?? col.width ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[key] : autoColWidths[key])
                    return (
                      <TableCell
                        key={col.key}
                        align={col.align}
                        className={isFrozen ? 'app-sticky-col-2' : undefined}
                        style={w ? { width: w, minWidth: w, maxWidth: w, ...(isFrozen ? { left: SELECT_COL_WIDTH } : {}) } : undefined}
                        sx={{
                          fontWeight: 700,
                          whiteSpace: 'nowrap',
                          py: 0.5,
                          bgcolor: 'background.paper',
                          borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {index === 0 ? 'Total' : val}
                      </TableCell>
                    )
                  })}
                </TableRow>
              )}
              itemContent={(_: number, row: SaleMoving) => (
                <>
                  <TableCell
                    padding="checkbox"
                    className="app-sticky-col"
                    style={{ width: SELECT_COL_WIDTH, minWidth: SELECT_COL_WIDTH }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Checkbox size="small" checked={selectedIds.has(row.id)} onChange={() => toggleId(row.id)} />
                  </TableCell>
                  {visibleColumns.map((col) => {
                    const key = String(col.key)
                    const isFrozen = FROZEN_KEYS.has(key)
                    const val = cellValue(row, col)
                    const w = getWidth(key) ?? col.width ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[key] : autoColWidths[key])
                    // Auto wrap mọi cột text (không kind số/ngày) — text dài xuống dòng
                    // thay vì cắt ellipsis; số/ngày giữ 1 dòng.
                    const wrap = col.wrap || getWidth(key) != null || !col.kind
                    const frozenStyle: CSSProperties = isFrozen ? { left: SELECT_COL_WIDTH } : {}
                    if (wrap) {
                      return (
                        <TableCell
                          key={col.key}
                          align={col.align}
                          className={isFrozen ? 'app-sticky-col-2' : undefined}
                          style={w ? { width: w, minWidth: w, maxWidth: w, ...frozenStyle } : frozenStyle}
                          sx={{ whiteSpace: 'normal', overflowWrap: 'anywhere', py: 0.5, verticalAlign: 'top' }}
                        >
                          <Box component="span" className="app-table-cell-wrap">
                            {val || '-'}
                          </Box>
                        </TableCell>
                      )
                    }
                    return (
                      <TableCell
                        key={col.key}
                        align={col.align}
                        className={isFrozen ? 'app-sticky-col-2' : undefined}
                        style={w ? { width: w, minWidth: w, maxWidth: w, ...frozenStyle } : frozenStyle}
                        sx={{ whiteSpace: 'nowrap', py: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'top' }}
                      >
                        <Box component="span" className="app-table-cell-text" title={val || '-'} sx={{ display: 'inline-block', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {val || '-'}
                        </Box>
                      </TableCell>
                    )
                  })}
                </>
              )}
              components={{
                Table: (props) => <Table {...props} size="small" stickyHeader sx={{ minWidth: minTableWidth, width: '100%' }} />,
                TableHead: ((props: object) => <TableHead {...props} component="thead" />) as never,
                TableBody: ((props: object) => <TableBody {...props} component="tbody" />) as never,
                TableRow: (props) => {
                  const idx = Number((props as unknown as Record<string, unknown>)['data-item-index'])
                  const rowId = Number.isFinite(idx) ? rows[idx]?.id : null
                  // Select CHỈ qua checkbox; click dòng = focus (enable Edit/Copy).
                  return (
                    <TableRow
                      {...props}
                      hover
                      selected={rowId != null && (selectedIds.has(rowId) || rowId === focusedValidId)}
                      sx={{ cursor: 'pointer' }}
                      onClick={() => toggleFocus(rowId ?? null)}
                    />
                  )
                },
              }}
            />
          )}
        </DataframeBody>
      </Box>

      <ConfirmDialog
        open={!!confirmDelete}
        title="Delete Moving"
        message={deleteMessage}
        destructive
        loading={deleting}
        onConfirm={() => void handleConfirmDelete()}
        onCancel={() => setConfirmDelete(null)}
      />
      {formState && (
        <MovingFormDialog
          open
          mode={formState.mode}
          movId={formState.movId}
          canEdit={canEdit}
          onClose={() => setFormState(null)}
          onSaved={() => setSelectedIds(new Set())}
        />
      )}
      <MovingPrintDialog
        open={printState.open}
        moveNos={printState.moveNos}
        canEdit={canEdit}
        onClose={() => setPrintState({ open: false, moveNos: [] })}
      />
      <FuturesColumnsMenu controller={columnsCtrl} />
    </Box>
  )
}
